import Page from "./Page";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Alert } from "@mui/material";
import Button from "../components/input/button";
import LoadingScreen from "../components/loading/LoadingScreen";
import useMessagesAPIClient from "../api/messages/hooks/useMessagesAPIClient";

const DeleteAccountPage = () => {
  const { activeUser } = useSelector((state) => state.user);
  const { submitDeletionRequest } = useMessagesAPIClient();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleDeleteRequest = async () => {
    if (!activeUser) return;

    setSubmitting(true);
    setError(null);

    try {
      await submitDeletionRequest({
        displayName: activeUser.name || activeUser.displayName,
        email: activeUser.email,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        "Failed to submit deletion request. Please try again or contact support directly at mindstackapp@gmail.com",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!activeUser) {
    return <LoadingScreen />;
  }

  return (
    <Page pageKey="delete-account" className="delete-account-page">
      <Box
        sx={{
          maxWidth: 700,
          mx: "auto",
          p: 3,
          backgroundColor: "#FFFFFF",
          borderRadius: 4,
        }}
      >
        <Typography variant="h1" gutterBottom>
          Delete Your Account
        </Typography>

        {!submitted ? (
          <>
            <Typography variant="p1" paragraph sx={{ mb: 2 }}>
              We're sorry to see you go. If you proceed with this request, the
              following will be permanently deleted:
            </Typography>

            <ul style={{ marginBottom: 32 }}>
              <li>
                <Typography variant="p1">
                  Your Mindstack account and profile information
                </Typography>
              </li>
              <li>
                <Typography variant="p1">All tasks you have created</Typography>
              </li>
              <li>
                <Typography variant="p1">
                  All labels and custom categories
                </Typography>
              </li>
              <li>
                <Typography variant="p1">
                  Your subscription information (if applicable)
                </Typography>
              </li>
              <li>
                <Typography variant="p1">
                  Any other data associated with your account
                </Typography>
              </li>
            </ul>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>This action cannot be undone.</strong> Once your account
                is deleted, all your data will be permanently removed from our
                servers and cannot be recovered.
              </Typography>
            </Alert>

            <Typography variant="p1" paragraph sx={{ mb: 2 }}>
              To request account deletion, click the button below. Our team will
              process your request and send you a confirmation email as soon as
              possible
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Account:{" "}
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {activeUser.email}
                </Box>
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              text={submitting ? "Submitting..." : "Request Account Deletion"}
              color="red"
              size="huge"
              onClick={handleDeleteRequest}
              disabled={submitting}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              If you have any questions or need assistance, please contact us at{" "}
              <a
                href="mailto:mindstackapp@gmail.com"
                style={{ color: "inherit" }}
              >
                mindstackapp@gmail.com
              </a>
            </Typography>
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="p1">
                Your account deletion request has been submitted successfully.
              </Typography>
            </Alert>

            <Typography variant="p1" paragraph>
              We have received your request to delete the account associated
              with{" "}
              <Box component="span" sx={{ fontWeight: "bold" }}>
                {activeUser.email}
              </Box>
              .
            </Typography>

            <Typography variant="p1" paragraph>
              Our team will process your request and you will receive a
              confirmation email once your account and all associated data have
              been permanently deleted. This typically takes up to 7 business
              days.
            </Typography>

            <Typography variant="p1" paragraph>
              If you have any questions in the meantime, please contact us at{" "}
              <a
                href="mailto:mindstackapp@gmail.com"
                style={{ color: "inherit" }}
              >
                mindstackapp@gmail.com
              </a>
            </Typography>
          </>
        )}
      </Box>
    </Page>
  );
};

export default DeleteAccountPage;
